import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import type { CheckIssue, CheckSeverity, CheckTab } from '../siteCheck';
import './checkPanel.css';

const SEVERITY_LABEL: Record<CheckSeverity, string> = {
  high: 'Fix first',
  medium: 'Worth doing',
  low: 'Nice to have'
};

const TAB_LABEL: Record<CheckTab, string> = {
  content: 'Content',
  items: 'Items',
  images: 'Images',
  links: 'Links',
  business: 'Business'
};

interface CheckPanelProps {
  ctx: PanelCtx;
  issues: CheckIssue[];
  onGoTo: (tab: CheckTab) => void;
}

export default function CheckPanel({ ctx, issues, onGoTo }: CheckPanelProps) {
  const high = issues.filter((issue) => issue.severity === 'high').length;

  const show = (issue: CheckIssue) => {
    if (issue.focus) ctx.post({ type: 'focus', selector: issue.focus });
  };

  return (
    <div className="dw-panel">
      <div className="dw-panel-heading">
        <Icon name="shield" size={18} />
        <h2>Check</h2>
      </div>

      {issues.length === 0 ? (
        <div className="dw-check-ready" role="status">
          <Icon name="check" size={22} />
          <div>
            <strong>Ready to share</strong>
            <p>Nothing left from the template that visitors would trip over.</p>
          </div>
        </div>
      ) : (
        <p className="dw-help-text" role="status">
          {high > 0
            ? `${high} ${high === 1 ? 'thing' : 'things'} to fix before you share, plus a few suggestions.`
            : 'Nothing serious. A few suggestions to make the page your own.'}
        </p>
      )}

      <ul className="dw-check-list">
        {issues.map((issue) => (
          <li key={issue.id} className={`dw-check-item dw-check-item--${issue.severity}`}>
            <span className="dw-check-severity">{SEVERITY_LABEL[issue.severity]}</span>
            <strong className="dw-check-title">{issue.title}</strong>
            <p className="dw-check-detail">{issue.detail}</p>
            {issue.examples?.length ? (
              <ul className="dw-check-examples" aria-label="For example">
                {issue.examples.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            ) : null}
            <div className="dw-check-actions">
              {issue.autoFix ? (
                <button
                  type="button"
                  className="dw-button dw-button--primary"
                  onClick={() => {
                    ctx.update(issue.autoFix!.patch);
                    // Text normally changes inside the preview; a change made here has to rebuild it.
                    ctx.reload();
                  }}
                >
                  {issue.autoFix.label}
                </button>
              ) : null}
              {issue.tab ? (
                <button
                  type="button"
                  className="dw-button dw-button--ghost"
                  onClick={() => {
                    onGoTo(issue.tab!);
                    show(issue);
                  }}
                >
                  Fix in {TAB_LABEL[issue.tab]}
                </button>
              ) : null}
              {issue.focus ? (
                <button type="button" className="dw-check-link" onClick={() => show(issue)}>
                  Show me
                </button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Shown on the Share tab, so problems are seen before a link goes out. */
export function CheckSummary({ issues, onReview }: { issues: CheckIssue[]; onReview: () => void }) {
  const high = issues.filter((issue) => issue.severity === 'high');
  if (issues.length === 0) {
    return (
      <div className="dw-check-summary dw-check-summary--ok">
        <Icon name="check" size={16} /> Checked: nothing left from the template that visitors would trip over.
      </div>
    );
  }
  return (
    <div className={`dw-check-summary ${high.length ? 'dw-check-summary--warn' : ''}`}>
      <Icon name={high.length ? 'alert' : 'info'} size={16} />
      <span>
        {high.length
          ? `Before you share: ${high.length} ${high.length === 1 ? 'thing' : 'things'} still show the template's sample details.`
          : `${issues.length} ${issues.length === 1 ? 'suggestion' : 'suggestions'} to make the page your own.`}
      </span>
      <button type="button" className="dw-check-link" onClick={onReview}>
        Review
      </button>
    </div>
  );
}
